"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { clearSSOToasts } from "@/components/SSOAuthButton/hooks/useSSOState";

import { Typography } from "@/components/Typography";
import { Form } from "@/components/Form";
import { Button } from "@/components/Button";
import { SSOAuthContainer } from "../SSOAuthButton";

import { getCookie } from "@/services/CookiesService";

import { loginValidation } from "@/utils/validators/login";
import { onBindClick } from "@/utils/formHelper";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import { useLogin } from "@/hooks/auth/useLogin";
import { useToast } from "@/hooks/ui/useToast";

import { LoginFormProps } from "./types";
import { QR_REFERENCE_COOKIE } from "@/containers/AuthContainer/types";
import { getSiteInfo } from "@/sites";
import { PAGES } from "@/internals/constants/pages";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { SSO_PROVIDER, type SsoProviderId } from "../SSOAuthButton/types";

export default function Login({
  onSetFormType,
  onLogin,
  setIsLoading,
  isLoading,
  loginData,
}: LoginFormProps) {
  const { toast } = useToast();
  const { t } = useTranslation(PAGES.LOGIN);

  const [isSsoLoading, setIsSsoLoading] = React.useState(false);

  onBindClick(loginData?.formFields, "forgotPasswordLink", () => onSetFormType("forgotPassword"));

  const schema = loginValidation(loginData);
  const { onHandleLogin, loginResponse, loginError } = useLogin();

  const {
    richtext: { Tag, content, spacing, className },
    button: { id, label },
  } = loginData?.authModeSwitcher || {};

  const [formObject, setFormObject] = React.useState<
    { values: FieldValues; methods: UseFormReturn } | undefined
  >(undefined);

  // Clear SSO toasts when Login component mounts (form type change)
  React.useEffect(() => {
    clearSSOToasts();
  }, []);

  const onLoginRequest = (values: FieldValues) => {
    setIsLoading(true);
    onHandleLogin({
      email: values.email,
      password: values.password,
      reference: getCookie(QR_REFERENCE_COOKIE),
    });
  };

  React.useEffect(() => {
    if (formObject && loginResponse?.status === 200) {
      onLogin(formObject.values, formObject.methods);
      setFormObject(undefined);
    }
  }, [loginResponse, formObject, onLogin]);

  React.useEffect(() => {
    if (formObject && loginError) {
      const status = loginError.status || loginError.statusCode;
      if (status === 400 || status === 401 || status === 404) {
        formObject.methods.setError("email", {
          message: loginData.invalidAuthMessage,
        });
        formObject.methods.resetField("password", { defaultValue: "" });
      } else {
        const errorMessage = ApiErrorMessageBuilder.buildToastMessage(loginError, {
          fallbackMessage: API_ERROR_FALLBACKS.AUTH.LOGIN,
        });

        toast({ message: errorMessage, type: "error" });
      }

      setIsLoading(false);
    }
  }, [loginError, formObject, loginData?.invalidAuthMessage, toast, setIsLoading]);

  const onLoginSubmit = (values: FieldValues, methods: UseFormReturn) => {
    if (isLoading) return;
    setFormObject({ values, methods });
    onLoginRequest(values);
  };

  const handleRegisterClick = () => {
    // Clear SSO toasts before switching forms
    clearSSOToasts();
    onSetFormType("register");
  };

  const onSsoCompleted = (provider: SsoProviderId, payload: unknown) => {
    switch (provider) {
      case SSO_PROVIDER.APPLE:
      case SSO_PROVIDER.GOOGLE:
      case SSO_PROVIDER.MICROSOFT: {
        void payload;
        onLogin({ provider, ...formObject?.values }, formObject?.methods);
        break;
      }

      default:
        void payload;
        break;
    }
  };

  const { roundedAuthButtons } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!);

  return (
    <div
      className="Login"
      data-testid="Login"
    >
      <Typography
        Tag="p"
        classname="Login__title"
        size="xl"
        spacing={5}
      >
        {loginData.title}
      </Typography>
      <SSOAuthContainer
        mode="signin"
        rounded={roundedAuthButtons}
        isFormLoading={isLoading}
        labels={{
          [SSO_PROVIDER.GOOGLE]: {
            label: t("google_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
          [SSO_PROVIDER.MICROSOFT]: {
            label: t("microsoft_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
          [SSO_PROVIDER.APPLE]: {
            label: t("apple_button_label") as string,
            loading: t("sso_button_loading_label") as string,
          },
        }}
        errors={{
          loginError: loginData.invalidAuthSSOMessage,
        }}
        onSsoLoadingChange={setIsSsoLoading}
        onSsoCompleted={onSsoCompleted}
      />

      <Form
        fields={loginData.formFields}
        onSubmit={onLoginSubmit}
        schema={schema}
        validationMode="onSubmit"
        isDisabled={isSsoLoading}
        isLoading={isLoading && ["email", "password", "login"]}
        isAuth
      />

      <div className="Auth__switcher">
        <Typography
          Tag={Tag}
          spacing={spacing}
          classname={className}
        >
          {content}
        </Typography>

        {/* //TODO: components-lib: This button will be replace by a Link Component */}
        <Button
          ghost
          buttonId={id}
          spacing={5}
          label={label}
          dataQA="login-modal-register-button"
          onClick={handleRegisterClick}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}
